import React, { ReactNode } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import SkuCopyRow from "../../../../../../../views/components/common/SkuCopyRow";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import FormTextField from "../../../../../../../views/components/common/form/FormTextField";
import {
  getModernPageColors,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTableRowSx,
} from "../../../../../../../styles/modernListingPageSx";
import {
  INVENTORY_ADJUSTMENT_REASON_MAX,
  SkuWarehouseSummary,
} from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";

/** Tables switch to stacked cards below `md` (phones and small tablets). */
export const useIsCompactLayout = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("md"));
};

interface MobileSelectAllBarProps {
  total: number;
  selectedCount: number;
  onToggle: (selected: boolean) => void;
  disabled?: boolean;
}

export const MobileSelectAllBar: React.FC<MobileSelectAllBarProps> = ({
  total,
  selectedCount,
  onToggle,
  disabled,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const allSelected = total > 0 && selectedCount === total;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1,
        py: 0.5,
        borderRadius: 2,
        bgcolor: colors.headerBg,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Checkbox
        size="small"
        checked={allSelected}
        indeterminate={selectedCount > 0 && !allSelected}
        onChange={(e) => onToggle(e.target.checked)}
        disabled={disabled}
      />
      <CustomTypography variant="heading8" color={colors.title}>
        Select all ({selectedCount}/{total} selected)
      </CustomTypography>
    </Box>
  );
};

interface MobileStockRowCardProps {
  sku: string;
  meta: { label: string; value: ReactNode }[];
  selected: boolean;
  onToggle: (selected: boolean) => void;
  disabled?: boolean;
  error?: string;
  children?: ReactNode;
}

/** Card version of a selectable box row for narrow screens. */
export const MobileStockRowCard: React.FC<MobileStockRowCardProps> = ({
  sku,
  meta,
  selected,
  onToggle,
  disabled,
  error,
  children,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${
          error ? theme.palette.error.main : selected ? colors.accent : colors.border
        }`,
        bgcolor: selected ? colors.surfaceSoft : colors.surface,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: -1 }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={disabled}
        />
        <SkuCopyRow
          sku={sku}
          variant="heading7"
          color={colors.title}
          fontWeight={600}
        />
      </Box>
      <Grid2 container spacing={1} sx={{ mt: 0.5 }}>
        {meta.map((item) => (
          <Grid2 key={item.label} size={{ xs: 6, sm: 3 }}>
            <CustomTypography variant="body3" color={colors.subtitle}>
              {item.label}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              color={colors.title}
              sx={{ wordBreak: "break-word" }}
            >
              {item.value}
            </CustomTypography>
          </Grid2>
        ))}
      </Grid2>
      {children && (
        <Grid2 container spacing={1.5} sx={{ mt: 1.5 }}>
          {children}
        </Grid2>
      )}
      <InlineFormError message={error} sx={{ mb: 0 }} />
    </Box>
  );
};

export const InlineFormSuccess: React.FC<{ message?: string }> = ({
  message,
}) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  if (!message) return null;
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: "6px",
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(16,185,129,0.16)"
            : c.successGreenLightBackground,
        border: `1px solid ${c.successGreenLightBorder}`,
      }}
    >
      <CustomTypography
        variant="heading8"
        color={c.successGreen}
        style={{ fontSize: "0.75rem", lineHeight: 1.45 }}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

interface ReasonFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
}

/** Mandatory multi-line reason that applies to the whole request. */
export const AdjustmentReasonField: React.FC<ReasonFieldProps> = ({
  value,
  onChange,
  disabled,
  label = "Reason",
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
        <CustomTypography variant="heading8" color={colors.label}>
          {label} *
        </CustomTypography>
        <CustomTypography variant="caption" color={colors.subtitle}>
          {value.length}/{INVENTORY_ADJUSTMENT_REASON_MAX}
        </CustomTypography>
      </Box>
      <FormTextField
        multiline
        minRows={3}
        maxRows={6}
        variant="outlined"
        color="secondary"
        fontSize="13px"
        placeholder="Why is this change being made? e.g. Cycle count correction - Oct 2026"
        value={value}
        maxLength={INVENTORY_ADJUSTMENT_REASON_MAX}
        onChange={(_id, next) => onChange(next || "")}
        disabled={disabled}
        sx={{ marginBottom: 0 }}
      />
    </Box>
  );
};

export const StockLoadingState: React.FC = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress />
  </Box>
);

export const StockEmptyState: React.FC<{ message: string }> = ({
  message,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: "8rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <CustomTypography
        variant="heading6"
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

/** On hand − blocked = available, per SKU × warehouse. */
export const SkuStockSummaryTable: React.FC<{
  summaries: SkuWarehouseSummary[];
}> = ({ summaries }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const headerSx = modernTableHeaderCellSx(theme);
  const cellSx = modernTableBodyCellSx(theme);
  const isCompact = useIsCompactLayout();

  if (isCompact) {
    return (
      <Box>
        <CustomTypography
          variant="heading7"
          color={colors.title}
          sx={{ mb: 1 }}
        >
          Stock by SKU
        </CustomTypography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxHeight: 260,
            overflow: "auto",
          }}
        >
          {summaries.map((summary) => (
            <Box
              key={summary.key}
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                bgcolor: colors.surface,
              }}
            >
              <SkuCopyRow
                sku={summary.sku}
                color={colors.title}
                fontWeight={700}
              />
              <CustomTypography variant="body3" color={colors.subtitle}>
                {summary.warehouse_id}
                {summary.city ? ` · ${summary.city}` : ""}
              </CustomTypography>
              <Grid2 container spacing={1} sx={{ mt: 0.75 }}>
                {[
                  { label: "On Hand", value: summary.on_hand },
                  {
                    label: "Blocked",
                    value: summary.blocked,
                    color: summary.blocked > 0 ? colors.error : undefined,
                  },
                  { label: "Available", value: summary.available },
                ].map((item) => (
                  <Grid2 key={item.label} size={4}>
                    <CustomTypography variant="body3" color={colors.subtitle}>
                      {item.label}
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      fontWeight={700}
                      color={item.color || colors.title}
                    >
                      {item.value}
                    </CustomTypography>
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <CustomTypography variant="heading7" color={colors.title} sx={{ mb: 1 }}>
        Stock by SKU
      </CustomTypography>
      <TableContainer
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          maxHeight: 220,
          overflow: "auto",
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerSx}>SKU</TableCell>
              <TableCell sx={headerSx}>Warehouse</TableCell>
              <TableCell sx={headerSx}>On Hand</TableCell>
              <TableCell sx={headerSx}>Blocked</TableCell>
              <TableCell sx={headerSx}>Available</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaries.map((summary) => (
              <TableRow key={summary.key} sx={modernTableRowSx(theme)}>
                <TableCell sx={cellSx}>
                  <SkuCopyRow
                    sku={summary.sku}
                    color={colors.title}
                    fontWeight={600}
                    variant="body3"
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  {summary.warehouse_id}
                  {summary.city ? ` · ${summary.city}` : ""}
                </TableCell>
                <TableCell sx={cellSx}>{summary.on_hand}</TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    color: summary.blocked > 0 ? colors.error : cellSx.color,
                    fontWeight: summary.blocked > 0 ? 600 : undefined,
                  }}
                >
                  {summary.blocked}
                </TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                  {summary.available}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
